import React, {Component} from 'react';
import * as R from 'ramda';
import {queryCampaign} from 'js/graphql/queries/queryCampaign';
import {
    transformGen3CampaignForVerifyReceipt,
    transformCampaignForVerifyReceipt
} from 'js/graphql/transforms/campaign';
import {numberWithCommas} from 'js/common/string_helper';

import 'scss/verify_receipt_common.scss';

import AdditionalNotes from './additional_notes';
import BidDetails from './bid_details';
import BrandDetails from './brand_details';
import GuidelinesDetails from './guidelines_details';
import SalesDetails from './sales_details';
import ShippingDetails from './shipping_details';
import TargetDetails from './target_details';
import hydrateAdminSession from 'js/admin_session';
import InvitedCreators from './invited_creators';
import {queryCampaignTargetingOptions} from '../../graphql/queries/queryCampaignTargetingOptions';
import TargetingOptionsDetails from './targeting_options_details';
import {queryFollowersTiersList} from '../../graphql/queries/queryFollowersTiers';
import {queryCampaignType} from 'js/graphql/queries/queryCampaignType';
import Instructions from './instructions';

export default class VerifyReceiptCommon extends Component {
    state = {
        gqlData: {},
        isGqlDataLoaded: false,
        campaignTargetingOptions: null
    };

    componentDidMount() {
        const {campaign} = this.props;
        if (!campaign.is_legacy_campaign) {
            this.getCampaignQuery(campaign.id);
        }
    }

    getCampaignQuery = async campaignId => {
        await hydrateAdminSession();
        const campaignData = await queryCampaign(campaignId);
        const campaignTargetingOptions = await queryCampaignTargetingOptions(campaignId);
        const followersTiers = await queryFollowersTiersList();

        const campaignType = await queryCampaignType(campaignId);

        let gqlData = {};

        if (campaignType) {
            gqlData.campaignType = campaignType;
        }

        if (campaignData.requiredHashtags) {
            gqlData.hashtags = campaignData.requiredHashtags.map(hash => hash.hashtag).join(',');
        }
        if (campaignData.requiredHandles) {
            gqlData.handles = campaignData.requiredHandles.map(handle => handle.handle).join(',');
        }

        if (campaignData.audienceGenders) {
            gqlData.gender = campaignData.audienceGenders[0].audienceGender;
        }

        if (!this.props.campaign.is_legacy_campaign && campaignData.creatorTargetFollowers) {
            const minFollowerCount = numberWithCommas(
                campaignData.creatorTargetFollowers.minFollowers
            );
            const maxFollowerCount = numberWithCommas(
                campaignData.creatorTargetFollowers.maxFollowers
            );
            /**
             * `numberWithCommas(input)` returns string value 0 when then input is null
             * maxFollowers would be `null` when the count is set for 500,000+
             * in that case we want to display `{minFollowerCount}+`
             * instead of `{minFollowerCount} - {maxFollowerCount}`
             */
            gqlData.followerCount = `${minFollowerCount}${
                maxFollowerCount !== '0' ? ' - ' + maxFollowerCount : '+'
            }`;
        }

        if (campaignData.audienceInterests) {
            gqlData.interests = campaignData.audienceInterests
                .map(interest => interest.interest)
                .join(',');
        }

        if (campaignData.audienceLanguages) {
            gqlData.languages = campaignData.audienceLanguages
                .map(language => language.audienceLanguage.code)
                .join(',');
        }

        if (campaignData.audienceAgeRanges) {
            gqlData.ageRanges = campaignData.audienceAgeRanges.map(age => {
                if (age.minAgeYearsInclusive === null && age.maxAgeYearsInclusive === null) {
                    return 'All';
                }
                if (age.maxAgeYearsInclusive === null) {
                    return `${age.minAgeYearsInclusive}+`;
                }
                if (age.minAgeYearsInclusive && age.maxAgeYearsInclusive) {
                    return `${age.minAgeYearsInclusive}-${age.maxAgeYearsInclusive}`;
                }
                return 'N/A';
            });
        }

        if (campaignData.audienceGeographies?.length) {
            const regions = [];
            const countries = [];
            const cities = [];
            const continents = [];

            campaignData.audienceGeographies.forEach(geos => {
                const geoComponent = geos.geographyComponent;
                switch (geoComponent.kind) {
                    case 'CONTINENT':
                        continents.push(geoComponent.continent);
                        break;
                    case 'COUNTRY':
                        countries.push(geoComponent.countryCode);
                        break;
                    case 'REGION':
                        regions.push(`${geoComponent.countryCode}: ${geoComponent.regionCode}`);
                        break;
                    case 'CITY':
                        cities.push(
                            // eslint-disable-next-line max-len
                            `${geoComponent.countryCode}: ${geoComponent.cityName}, ${geoComponent.regionCode}`
                        );
                        break;
                }
            });

            if (cities.length) {
                gqlData.audienceLocation = cities;
            } else if (regions.length) {
                gqlData.audienceLocation = regions;
            } else if (countries.length) {
                gqlData.audienceLocation = countries;
            } else if (continents.length) {
                gqlData.audienceLocation = continents;
            }
        }

        if (
            campaignData.deliverableDescriptors &&
            campaignData.deliverableDescriptors.edges.length
        ) {
            gqlData = {...gqlData, ...(await transformGen3CampaignForVerifyReceipt(campaignData))};
        } else if (campaignData.productions && campaignData.productions.edges.length > 0) {
            gqlData = {...gqlData, ...(await transformCampaignForVerifyReceipt(campaignData))};
        }

        if (campaignData.estimatedAdBudget) {
            gqlData.estimatedAdBudget = campaignData.estimatedAdBudget;
        }

        if (campaignData.campaignKpi) {
            let prefix = '';
            switch (campaignData.campaignKpi.subCategory.kpiValueKind) {
                case 'CONVERSIONS':
                    prefix = ' conversions';
                    break;
                case 'VIEWS':
                    prefix = ' views';
                    break;
                case 'PERCENT':
                    prefix = '%';
                    break;
                case 'NONE':
                    prefix = '';
                    break;
            }

            gqlData.campaignKpi = {
                campaignPrimaryKpi: campaignData.campaignKpi.subCategory.subCategoryName,
                campaignBenchmark: campaignData.campaignKpi.kpiBenchmarkValue,
                prefix: prefix
            };
        }

        if (followersTiers && campaignTargetingOptions?.followersTiers) {
            const campaignFollowersTier = [];

            campaignTargetingOptions.followersTiers.forEach(followerTier => {
                const followersTier = followersTiers.find(tier => tier.name === followerTier);
                if (followersTier) {
                    campaignFollowersTier.push(followersTier);
                }
            });
            campaignTargetingOptions.followersTiers = campaignFollowersTier;
        }

        this.setState({
            gqlData,
            isGqlDataLoaded: true,
            campaignTargetingOptions
        });
    };

    render() {
        const {
            brand,
            campaign,
            salesDetails,
            shippingDetails,
            enums,
            maps,
            campaignRoles
        } = this.props;
        const {gqlData, isGqlDataLoaded, campaignTargetingOptions} = this.state;

        return (
            <div className="verify-receipt-details-common">
                <div className="brand-target-product-details">
                    <BrandDetails
                        brandName={campaign.brand_name}
                        type={campaign.type}
                        brandIndustry={brand.industry}
                        contentUse={campaign.content_use}
                        platformHandle={campaign.platform_handle}
                        enums={enums}
                        maps={maps}
                        campaignDisplayType={campaign.campaign_display_type}
                        gqlData={gqlData}
                        isLegacyCampaign={campaign.is_legacy_campaign}
                    />

                    {campaignTargetingOptions ? (
                        <TargetingOptionsDetails details={campaignTargetingOptions} />
                    ) : (
                        <TargetDetails
                            audienceLocation={
                                gqlData.audienceLocation || campaign.audience_location
                            }
                            audienceLocationCustom={campaign.audience_location_custom}
                            audienceGender={gqlData.gender || campaign.audience_gender}
                            audienceLanguages={gqlData.languages || campaign.audience_languages}
                            audienceAge={gqlData.ageRanges || campaign.audience_age}
                            audienceInterests={gqlData.interests || campaign.audience_interests}
                            followerCount={gqlData.followerCount}
                            age21Plus={campaign.age_21_plus}
                            influencerFamilyFriendly={campaign.influencer_family_friendly}
                            creatorFraudDetectionEnabled={campaign.creator_fraud_detection_enabled}
                            gqlData={gqlData}
                            isLegacyCampaign={campaign.is_legacy_campaign}
                        />
                    )}

                    <BidDetails enums={enums} campaign={campaign} warnSaasCampaign />

                    {isGqlDataLoaded && <InvitedCreators campaignId={campaign.id} />}
                </div>

                <div className="vertical-divider" />

                <div className="center-details">
                    <GuidelinesDetails
                        campaignId={campaign.id}
                        campaignName={campaign.name}
                        campaignDisplayType={campaign.campaign_display_type}
                        description={campaign.short_description}
                        guidelines={campaign.guidelines}
                        hashtags={gqlData.hashtags || campaign.hashtags}
                        hideScreenNames={false}
                        screenNames={gqlData.handles || campaign.screen_names}
                        samplePostFilename={campaign.sample_post_filename}
                        samplePostImages={campaign.sample_post_images}
                        sampleVideoContent={campaign.sample_video_content}
                        type={gqlData.campaignType}
                        campaignContentWorkflow={campaign.campaign_content_workflow}
                        maps={maps}
                        gqlData={gqlData}
                        isLegacyCampaign={campaign.is_legacy_campaign}
                    />
                    <div className="horizontal-divider" />
                    <div className="additional-details">
                        <div className="shipping-sales-details">
                            <ShippingDetails
                                shippingDetails={R.isEmpty(shippingDetails) ? {} : shippingDetails}
                                enums={enums}
                                productName={campaign.product_name}
                                productDescription={campaign.product_description}
                                productRetailCost={campaign.product_retail_cost}
                                numberOfUnits={shippingDetails.number_of_units}
                                paymentStructure={shippingDetails.payment_structure}
                                received={shippingDetails.received}
                                scheduledArrival={shippingDetails.scheduled_arrival}
                                shippingPriority={shippingDetails.shipping_priority}
                            />
                            <div className="horizontal-divider" />
                            <SalesDetails
                                campaignRoles={campaignRoles}
                                salesDetails={salesDetails}
                                campaign={campaign}
                            />
                        </div>
                        <div className="vertical-divider" />
                        <div className="additional-notes">
                            <AdditionalNotes notes={campaign.notes} />
                            <Instructions instructions={campaign.instructions} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./components/verify_receipt_common/index.js